/* eslint-disable no-undef */

import { version, useEffect, useRef, useCallback, useState } from "react"

const scriptUrl = "https://maps.googleapis.com/maps/api/js"

export default function usePlacesWidget(props) {
  const {
    onPlaceSelected,
    apiKey,
    libraries = "places",
    inputAutocompleteValue = "new-password",
    options: {
      types = ["(cities)"],
      componentRestrictions,
      fields = [
        "address_components",
        "geometry.location",
        "place_id",
        "formatted_address",
      ],
      bounds,
      ...options
    } = {},
    googleMapsScriptBaseUrl = scriptUrl,
    language,
    setShards,
  } = props
  const [inputRef, setRef] = useState(null)
  const event = useRef(null)
  const autocompleteRef = useRef(null)
  const observerHack = useRef(null)
  const languageQueryParam = language ? `&language=${language}` : ""
  const googleMapsScriptUrl = `${googleMapsScriptBaseUrl}?libraries=${libraries}&key=${apiKey}&loading=async${languageQueryParam}`

  const handleLoadScript = useCallback(
    () => loadGoogleMapScript(googleMapsScriptBaseUrl, googleMapsScriptUrl),
    [googleMapsScriptBaseUrl, googleMapsScriptUrl]
  )

  useEffect(() => {
    const config = {
      ...options,
      bounds,
      fields,
      types,
    }

    if (componentRestrictions) {
      config.componentRestrictions = componentRestrictions
    }

    if (autocompleteRef.current || !inputRef || !isBrowser) return

    const handleAutoComplete = () => {
      if (typeof google === "undefined")
        return console.error(
          "Google has not been found. Make sure your provide apiKey prop."
        )

      if (!google.maps?.places)
        return console.error("Google maps places API must be loaded.")

      if (!(inputRef instanceof HTMLInputElement))
        return console.error("Input ref must be HTMLInputElement.")

      autocompleteRef.current = new google.maps.places.Autocomplete(
        inputRef,
        config
      )

      if (autocompleteRef.current) {
        event.current = autocompleteRef.current.addListener(
          "place_changed",
          () => {
            if (onPlaceSelected && autocompleteRef && autocompleteRef.current) {
              onPlaceSelected(autocompleteRef.current.getPlace())
            }
          }
        )

        if (typeof setShards === "function") {
          inputRef.addEventListener("focus", () => {
            const pacContainerEl = document.querySelector(".pac-container")
            if (pacContainerEl) {
              setShards([pacContainerEl])
            }
          })
        }

        inputRef.addEventListener("keydown", (e) => {
          // Prevent Enter/Escape key from submitting form or dismissing modal
          const pacContainerEl = document.querySelector(".pac-container")
          if (
            pacContainerEl?.offsetParent !== null &&
            (e.key === "Enter" || e.key === "Escape")
          ) {
            e.preventDefault()
          }
        })
      }
    }

    if (apiKey) {
      handleLoadScript().then(() => handleAutoComplete())
    } else {
      handleAutoComplete()
    }

    return () => (event.current ? event.current.remove() : undefined)
  }, [inputRef])

  // Autofill workaround adapted from https://stackoverflow.com/questions/29931712/chrome-autofill-covers-autocomplete-for-google-maps-api-v3/49161445#49161445
  useEffect(() => {
    // TODO find out why react 18(strict mode) hangs the page loading
    if (
      !version?.startsWith("18") &&
      isBrowser &&
      window.MutationObserver &&
      inputRef &&
      inputRef instanceof HTMLInputElement
    ) {
      observerHack.current = new MutationObserver(() => {
        observerHack.current.disconnect()

        if (inputRef) {
          inputRef.autocomplete = inputAutocompleteValue
        }
      })
      observerHack.current.observe(inputRef, {
        attributeFilter: ["autocomplete"],
        attributes: true,
      })
    }
  }, [inputAutocompleteValue])

  useEffect(() => {
    if (autocompleteRef.current) {
      autocompleteRef.current.setFields(fields)
    }
  }, [fields])

  useEffect(() => {
    if (autocompleteRef.current) {
      autocompleteRef.current.setBounds(bounds)
    }
  }, [bounds])

  useEffect(() => {
    if (autocompleteRef.current) {
      autocompleteRef.current.setComponentRestrictions(componentRestrictions)
    }
  }, [componentRestrictions])

  useEffect(() => {
    if (autocompleteRef.current) {
      autocompleteRef.current.setOptions(options)
    }
  }, [options])

  return {
    autocompleteRef,
    ref: inputRef,
    setRef,
  }
}

export const isBrowser = typeof window !== "undefined" && window.document

export const loadGoogleMapScript = (
  googleMapsScriptBaseUrl,
  googleMapsScriptUrl
) => {
  if (!isBrowser) return Promise.resolve()

  if (typeof google !== "undefined") {
    if (google.maps && google.maps.api) return Promise.resolve()
  }

  const scriptElements = document.querySelectorAll(
    `script[src*="${googleMapsScriptBaseUrl}"]`
  )

  if (scriptElements && scriptElements.length) {
    return new Promise((resolve) => {
      // in case we already have a script on the page and it's loaded we resolve
      if (typeof google !== "undefined") return resolve()

      // otherwise we wait until it's loaded and resolve
      scriptElements[0].addEventListener("load", () => resolve())
    })
  }

  let scriptUrl = new URL(googleMapsScriptUrl)
  scriptUrl.searchParams.set("callback", "__gac_callback__")
  const el = document.createElement("script")
  el.src = scriptUrl.toString()
  el.async = true

  return new Promise((resolve) => {
    window.__gac_callback__ = resolve
    document.head.appendChild(el)
  })
}
